import React, { Component } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import cc, { CurrencyCodeRecord } from 'currency-codes';

import { Td, Tr } from '../../../components/Table';
import { FormattedInput, ValidationState, Checkbox } from '../../../components/Forms';
import { Money } from '../../../components/Formatters';
import { CaptureState, NewOperationItem, RefundState } from '../../types';
import { PayoutFeeSplit, PayoutSplit, Transaction } from '../../../types/checkout';
import { fromMonetaryAmountString, toMonetaryAmountString } from '../../../helpers/formatters';
import { fromQuantityString } from '../../../helpers/formatters/money';
import { toQuantityString } from '../../../helpers/formatters/quantity';
import { getLocaleNumeralDelimiter, getLocaleNumeralDecimal } from '../../../helpers/intl';
import { getSkewedSplits } from '../../splitPaymentHelpers';
import RefundSplitPaymentRow from './RefundSplitPaymentRow';

export interface RefundItemProps extends WithTranslation {
    item: NewOperationItem;
    transaction: Transaction;
    onChange: (item: NewOperationItem) => void;
    toggleSplit: () => void;
    isSplitOpen: boolean;
}

const getRowType = (item: NewOperationItem) => {
    if (item.persisted.captureState === CaptureState.Authorized) {
        return UncapturedItemTr;
    }
    if (item.persisted.refundState === RefundState.Refunded) {
        return RefundedItemTr;
    }
    return ItemTr;
};

const getNextSplits = (item: NewOperationItem, nextAmount: number) => {
    if (item.persisted.orderItem.splits) {
        if (!item.operation.splits || item.operation.splits.length === 0) {
            // calculate splits using the default splits if there are no operation splits
            return getSkewedSplits(item.persisted.refundableSplits || [], nextAmount/item.persisted.refundableAmount, nextAmount);
        }
        // calculate splits using operation splits if defined so if the user has edited the splits they keep their changes
        return getSkewedSplits(item.operation.splits || [], nextAmount/item.operation.amount, nextAmount);

    }
    return [];
};

class RefundItemRow extends Component<RefundItemProps, { quantity: string }> {
    constructor(props: RefundItemProps, context: any) {
        super(props, context);
        this.toggleItem = this.toggleItem.bind(this);
        this.changeQuantity = this.changeQuantity.bind(this);
        this.changeAmount = this.changeAmount.bind(this);
    }

    toggleItem = () => {
        const { item, onChange } = this.props;
        const value = item.operation.amount === 0;
        const amount = value ? item.persisted.refundableAmount : 0;
        const quantity = value ? item.persisted.refundableQuantity : 0;
        const splits = value ? item.persisted.refundableSplits : [];
        this.setState({ quantity: quantity === 0 ? '' : quantity.toString() });
        onChange({
            ...item,
            operation: {
                amount,
                quantity,
                splits,
                feeSplit: item.operation.feeSplit,
            },
        });
    };

    changeQuantity = (value: string) => {
        const { item, onChange, i18n } = this.props;
        const unitPrice = item.persisted.refundableAmount / item.persisted.refundableQuantity;
        const quantity = fromQuantityString(i18n.language, value);
        const amount = Math.floor(quantity * unitPrice);
        const splits = getNextSplits(item, amount);
        onChange({
            ...item,
            operation: {
                amount: Math.round(amount),
                quantity: quantity,
                splits,
                feeSplit: item.operation.feeSplit,
            },
        });
    };

    changeAmount = (value: string) => {
        const { transaction, item, onChange, i18n } = this.props;
        const amount =  fromMonetaryAmountString(i18n.language, value, transaction.currency);
        const humanAmount = value;
        const splits = getNextSplits(item, amount);

        onChange({
            ...item,
            operation: {
                quantity: item.operation.quantity,
                amount,
                humanAmount,
                splits,
                feeSplit: item.operation.feeSplit,
            },
        });
    };

    changeSplits = (splits: PayoutSplit[], feeSplit: PayoutFeeSplit) => {
        const { item, onChange } = this.props;
        onChange({
            ...item,
            operation: {
                ...(item.operation),
                splits,
                feeSplit,
            },
        });
    };

    render() {
        const { item, i18n, transaction, isSplitOpen, toggleSplit } = this.props;
        const lng = i18n.language;
        const orderItem = item.persisted.orderItem;
        const disabled =
            item.persisted.refundState === RefundState.Refunded ||
            item.persisted.captureState === CaptureState.Authorized;
        const canOnlyRefundFullRows = transaction.payment_product === 'instabank';
        const exponent = cc.code(transaction.currency)
            ? (cc.code(transaction.currency) as CurrencyCodeRecord).digits
            : 2;

        const Row = getRowType(item);

        const delimiter = getLocaleNumeralDelimiter(lng);
        const numeralDecimalMark = getLocaleNumeralDecimal(lng);

        const hasError = item.operation.quantity > item.persisted.refundableQuantity || item.operation.amount > item.persisted.refundableAmount;

        return (
            <>
                <Row key={orderItem.line_id}>
                    <ItemTd className="checkbox">
                        <Checkbox
                            disabled={disabled}
                            className={item.persisted.captureState === CaptureState.PartiallyCaptured ? 'half' : ''}
                            name={orderItem.line_id || ''}
                            label={null}
                            checked={
                                item.operation.amount > 0 ||
                            item.operation.quantity > 0 ||
                            item.persisted.refundState === RefundState.Refunded
                            }
                            onChange={this.toggleItem}
                        />
                    </ItemTd>
                    <ItemTd className={'id'}>{orderItem.id}</ItemTd>
                    <ItemTd className={'description'}>{orderItem.description}</ItemTd>
                    <ItemTd className="quantity">
                        <QuantityInputWrapper>
                            <FormattedInput
                                options={{
                                    numeral: true,
                                    numericOnly: true,
                                    numeralThousandsGroupStyle: 'thousand',
                                    delimiter,
                                    numeralIntegerScale: `${orderItem.amount}`.length,
                                    numeralDecimalMark,
                                    numeralPositiveOnly: true,
                                    numeralDecimalScale: exponent,
                                }}
                                autoComplete="off"
                                placeholder="0"
                                type="tel"
                                inputMode="numeric"
                                name="amountToCapture"
                                value={item.operation.quantity === 0 ? '' : toQuantityString(lng, item.operation.quantity)}
                                onChange={(e: React.FormEvent<HTMLInputElement>) => {
                                    this.changeQuantity(e.currentTarget.value);
                                }}
                                onBlur={(e: React.FormEvent<HTMLInputElement>) => {
                                    this.changeQuantity(e.currentTarget.value);
                                }}
                                disabled={disabled || canOnlyRefundFullRows}
                                validation={
                                    item.operation.quantity > item.persisted.refundableQuantity
                                        ? {
                                            state: ValidationState.Invalid,
                                            message: '',
                                        }
                                        : undefined
                                }
                            />
                        </QuantityInputWrapper>
                        {' / '}
                        {item.persisted.refundableQuantity}
                    </ItemTd>
                    <ItemTd className="amount">
                        <AmountInputWrapper>
                            <FormattedInput
                                options={{
                                    numeral: true,
                                    numericOnly: true,
                                    numeralThousandsGroupStyle: 'thousand',
                                    delimiter,
                                    numeralIntegerScale: `${orderItem.amount}`.length,
                                    numeralDecimalMark,
                                    numeralPositiveOnly: true,
                                    numeralDecimalScale: exponent,
                                }}
                                autoComplete="off"
                                placeholder={toMonetaryAmountString(lng, 0, transaction.currency)}
                                type="tel"
                                inputMode="numeric"
                                name="amountToCapture"
                                value={
                                    item.operation.amount === 0
                                        ? ''
                                        : (item.operation.humanAmount || toMonetaryAmountString(lng, item.operation.amount, transaction.currency))
                                }
                                onChange={(e: React.FormEvent<HTMLInputElement>) => {
                                    this.changeAmount(e.currentTarget.value);
                                }}
                                onBlur={(e: React.FormEvent<HTMLInputElement>) => {
                                    this.changeAmount(e.currentTarget.value);
                                }}
                                validation={
                                    item.operation.amount > item.persisted.refundableAmount
                                        ? {
                                            state: ValidationState.Invalid,
                                            message: '',
                                        }
                                        : undefined
                                }
                                disabled={disabled || canOnlyRefundFullRows}
                            />
                        </AmountInputWrapper>
                        {' / '}
                        <Money amount={item.persisted.refundableAmount} currency={transaction.currency} />
                    </ItemTd>
                </Row>
                {orderItem.splits && <ItemTr>
                    <ItemTd colSpan={6}>
                        <RefundSplitPaymentRow
                            changeSplits={this.changeSplits}
                            payoutSplits={item.operation.splits || []}
                            currency={transaction.currency}
                            amount={item.operation.amount || 0}
                            open={isSplitOpen}
                            toggleSplit={() => !hasError && toggleSplit()}
                            feeSplit={item.operation.feeSplit || { type: 'proportional', destinations: []}}
                        />
                    </ItemTd>
                </ItemTr>}
            </>
        );
    }
}

export default withTranslation()<React.ComponentType<RefundItemProps>>(RefundItemRow);

const ItemTd = styled(Td)`
    &.amount {
        width: 30%;
    }
    &.quantity {
        width: 17%;
    }
    &.description {
        width: 38%;
        user-select: none;
    }
    &.id {
        width: 10%;
        user-select: none;
    }
    &.checkbox {
        width: 5%;
    }
    &.interactive {
        cursor: pointer;
    }
`;

const QuantityInputWrapper = styled.span`
    width: 50%;
    display: inline-block;
    padding-top: 18px;
`;

const AmountInputWrapper = styled.span`
    width: 50%;
    display: inline-block;
    padding-top: 18px;
`;

const ItemTr = styled(Tr)`
    > td {
        padding-top: 0px !important;
        padding-bottom: 0px !important;
    }
`;

const RefundedItemTr = styled(ItemTr)`
    opacity: 0.5;
    > td {
        text-decoration: line-through;
    }
`;

const UncapturedItemTr = styled(ItemTr)`
    opacity: 0.5;
`;
